// @flow

import * as React from 'react';

import { CircleUserRound } from 'lucide-react';

import { useLocation } from 'react-router-dom';
import { compose } from 'redux';

import {
  selectIsAuthenticatedUser,
  selectIsSudoedUser,
} from '@gandi/authentication';
import { selectPersonal } from '@gandi/redux-sharing';
import { useTranslator } from '@gandi/translate';

import { Button } from '@gandi/ui';
import { DropdownMenuTab, DropdownMenuTabItem } from '@gandi/ui/layout';

import { withBasename, type WithBasenameProps } from 'src/helpers/withBasename';

import { useAppSelector } from 'src/hooks/useAppSelector';
import { selectUserItems } from 'src/modules/app';

import styles from './UserWidget.scss';

type InjectedProps = {
  ...WithBasenameProps,
};

export type Props = InjectedProps;

function UserWidget({
  basename,
}: Props): React.Node | Array<React.MixedElement> {
  const { __ } = useTranslator();
  const { pathname, search } = useLocation();
  const isAuthenticated = useAppSelector(selectIsAuthenticatedUser);
  const items = useAppSelector(state =>
    selectUserItems(state, { __, basename, pathname, search }),
  );
  const buttonTitle = __(
    '<for-translators-use>Admin accessibility landmark</for-translators-use>User menu',
  );

  const isSudo = useAppSelector(selectIsSudoedUser);

  const {
    firstname,
    lastname,
    /** `firstname` & `lastname` may not be known yet before the first purchase;
     * but `name` is the username which is always defined when authenticated.
     */
    name: username,
    email,
  } = useAppSelector(selectPersonal) || {};
  // Shorten the username in sudo when it's a SAML email address
  const isSaml = username?.includes('@');
  const displayname = username?.split('@').shift();

  const fullname =
    firstname && lastname && (isSudo || isSaml)
      ? `${firstname} ${lastname}`
      : username;

  if (!isAuthenticated) {
    return items.map(item => (
      <Button
        key={`useritem-${item.title}`}
        kind="secondary"
        href={item.href}
        to={item.to}
        title={item.title}
      >
        {item.title}
      </Button>
    ));
  }

  return (
    <DropdownMenuTab
      iconSecondaryMenuButton={
        isSudo ? (
          <CircleUserRound size={20} color="#139a9a" />
        ) : (
          <CircleUserRound size={20} />
        )
      }
      alignDDRight
      label={(isSudo && displayname) || ''}
      id="user-menu"
      withSecondaryMenuButton
      // $FlowFixMe[incompatible-type]
      title={`${buttonTitle}, ${username}`}
    >
      <DropdownMenuTabItem withSeparator>
        <div className={styles.userMenuCustomItemCtn}>
          <div className={styles.userNameCtn}>
            <CircleUserRound size="20" />
            <p className={styles.userName}>
              <strong>{fullname}</strong>
            </p>
          </div>
          <p className={styles.userEmail}>{email}</p>
        </div>
      </DropdownMenuTabItem>
      {items.map(item => (
        <DropdownMenuTabItem
          key={item.title}
          href={item.href}
          to={item.to}
          icon={item.icon ? <item.icon /> : undefined}
        >
          {item.title}
        </DropdownMenuTabItem>
      ))}
    </DropdownMenuTab>
  );
}

export { UserWidget as Presenter };

export default (compose(withBasename)(UserWidget): React.ComponentType<{}>);
